import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Sparkles, ArrowRight, Loader2, Rocket, CreditCard } from "lucide-react";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

/**
 * CrossSellCard — surfaces one or two smart "add-on" service suggestions on
 * `/my` based on the user's current tier + latest audit signal. Each
 * suggestion is dismissible; dismissed slugs are remembered per user.
 */
export default function CrossSellCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  const load = () => {
    axios.get(`${API}/my/cross-sell`, { headers: httpAuth() })
      .then((r) => setData(r.data))
      .catch(() => setData({ suggestions: [] }));
  };
  useEffect(load, []);

  if (!data || (data.suggestions || []).length === 0) return null;

  const dismiss = async (slug) => {
    setBusy(slug);
    try {
      await axios.post(`${API}/my/cross-sell/dismiss`, { slug },
        { headers: httpAuth() });
      // Optimistic hide
      setData((d) => ({ ...d,
        suggestions: (d.suggestions || []).filter((s) => s.slug !== slug) }));
      toast.success("We won't suggest that one again.");
    } catch { toast.error("Could not dismiss"); }
    finally { setBusy(null); }
  };

  const upgradeToGrow = async () => {
    setUpgrading(true);
    try {
      const r = await axios.post(`${API}/packages/upgrade`,
        { package: "grow", origin: window.location.origin },
        { headers: httpAuth() });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else { toast.error("Could not start upgrade"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed to start");
    } finally { setUpgrading(false); }
  };

  const canUpgrade = data.tier === "start";

  return (
    <div className="mt-8" data-testid="cross-sell-card">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-orange-500" />
        <h3 className="text-sm font-bold text-slate-800">Smart next steps for your {data.tier_label} plan</h3>
        <span className="text-[11px] text-slate-400 ml-auto inline-flex items-center gap-1"
               title="Coming soon — email hello@websitetech.pro for changes"
               data-testid="manage-billing-link">
          <CreditCard className="w-3 h-3" /> Manage billing <span className="italic">(coming soon)</span>
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.suggestions.map((s) => (
          <Card key={s.slug}
                 className="p-5 border border-slate-200 rounded-2xl relative bg-gradient-to-br from-white via-orange-50/40 to-white hover:border-orange-300 transition-colors"
                 data-testid={`cross-sell-${s.slug}`}>
            <button onClick={() => dismiss(s.slug)}
                     disabled={busy === s.slug}
                     className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100"
                     aria-label="Dismiss"
                     data-testid={`cross-sell-dismiss-${s.slug}`}>
              {busy === s.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
            </button>
            <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">
              Next best step
            </div>
            <h4 className="text-lg font-bold text-slate-900 mt-1">{s.title}</h4>
            <p className="text-sm text-slate-600 mt-1.5 leading-snug">{s.reason}</p>
            <Link to={s.url}
                   className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-orange-700 hover:text-orange-800"
                   data-testid={`cross-sell-cta-${s.slug}`}>
              Learn how it lifts my numbers <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            {canUpgrade && s.slug === "local-business" && (
              <button onClick={upgradeToGrow}
                       disabled={upgrading}
                       className="mt-3 ml-3 inline-flex items-center gap-1 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-3 py-1.5 disabled:opacity-60"
                       data-testid="cross-sell-upgrade-grow">
                {upgrading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                Upgrade to GROW now
              </button>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
